import {
  Action,
  ActionReducer,
  combineReducers,
  createFeatureSelector,
  MetaReducer,
} from "@ngrx/store";
import { diff } from "deep-object-diff";
import * as fromRoot from "../../../reducers";
import * as fromTreatmentContract from "./contracts/treatment-contract.reducer";
import * as fromCourtesy from "./courtesies/courtesy.reducer";
import * as fromDiagnosisCode from "./diagnosis-codes/diagnosis-code.reducer";
import * as fromDoctor from "./doctors/doctor.reducer";
import * as fromFee from "./fees/fee.reducer";
import * as fromGuardian from "./guardians/guardian.reducer";
import * as fromInsurancePolicy from "./insurance-policies/insurance-policy.reducer";
import * as fromInsurancePolicyBenefit from "./insurance-policy-benefits/insurance-policy-benefit.reducer";
import * as fromTreatmentNotification from "./notifications/treatment-notification.reducer";
import * as fromPatientExam from "./patient-exams/patient-exam.reducer";
import * as fromPracticeCourtesy from "./practice-courtesies/practice-courtesy.reducer";
import * as fromPracticeFee from "./practice-fees/practice-fee.reducer";
import * as fromProcedure from "./procedures/procedure.reducer";
import * as fromTreatmentPlanBenefit from "./treatment-plan-benefits/treatment-plan-benefit.reducer";
import * as fromTreatmentPlan from "./treatment-plans/treatment-plan.reducer";
import * as fromTreatment from "./treatments/treatment.reducer";
import * as fromTxCoordinator from "./tx-coordinators/tx-coordinator.reducer";
import * as fromConfiguration from "./configuration/configuration.reducer";
import * as fromCopayProcedureFee from "./copay-procedure-fees/copay-procedure-fee.reducer";
import * as fromLineItem from "./line-items/line-item.reducer";
import * as fromInsuranceCarrier from "./insurance-carrier/insurance-carrier.reducer";
import * as fromImaxx from "./imaxx/imaxx.reducer";
import * as fromImaxxEligible from "./imaxx-eligible/imaxx-eligible.reducer";
import * as fromModuleConfiguration from "./module-configuration/module-configuration.reducer";

export const featureKey = "treatmentDetails";

export interface TreatmentDetailState {
  [fromConfiguration.featureKey]: fromConfiguration.State;
  [fromCourtesy.featureKey]: fromCourtesy.CourtesyState;
  [fromFee.featureKey]: fromFee.FeeState;
  [fromPatientExam.featureKey]: fromPatientExam.PatientExamState;
  [fromTreatmentContract.featureKey]: fromTreatmentContract.TreatmentContractState;
  [fromTreatmentPlan.featureKey]: fromTreatmentPlan.TreatmentPlanState;

  [fromDiagnosisCode.featureKey]: fromDiagnosisCode.DiagnosisCodeState;
  [fromDoctor.featureKey]: fromDoctor.DoctorState;
  [fromGuardian.featureKey]: fromGuardian.GuardianState;
  [fromPracticeCourtesy.featureKey]: fromPracticeCourtesy.PracticeCourtesyState;
  [fromPracticeFee.featureKey]: fromPracticeFee.PracticeFeeState;
  [fromProcedure.featureKey]: fromProcedure.ProcedureState;
  [fromTreatment.featureKey]: fromTreatment.TreatmentState;
  [fromTxCoordinator.featureKey]: fromTxCoordinator.TxCoordinatorState;

  [fromInsurancePolicy.featureKey]: fromInsurancePolicy.InsurancePolicyState;
  [fromInsurancePolicyBenefit.featureKey]: fromInsurancePolicyBenefit.InsurancePolicyBenefitState;
  [fromTreatmentNotification.featureKey]: fromTreatmentNotification.TreatmentNotificationState;
  [fromTreatmentPlanBenefit.featureKey]: fromTreatmentPlanBenefit.State;
  [fromLineItem.featureKey]: fromLineItem.LineItemState;

  [fromCopayProcedureFee.featureKey]: fromCopayProcedureFee.CopayProcedureFeeState;
  [fromInsuranceCarrier.featureKey]: fromInsuranceCarrier.InsuranceCarrierState;

  [fromImaxx.featureKey]: fromImaxx.ImaxxState;
  [fromImaxxEligible.featureKey]: fromImaxxEligible.ImaxxEligibleState;
  [fromModuleConfiguration.featureKey]: fromModuleConfiguration.ModuleConfigurationState;
}

export interface State extends fromRoot.RootState {
  [featureKey]: TreatmentDetailState;
}

/**
 * The reducers for use on the Treatment Details page.
 *
 * By default, @ngrx/store uses generally uses combineReducers with the reducer map to compose the root reducer. The
 * reducers here are not necessarily the root reducer, but it is where most of the reducers for the app live and likely
 * where any new reducers should be added (unless adding a new page or a lazy loaded module.)
 */
// eslint-disable-next-line
export function reducers(
  state: TreatmentDetailState | undefined,
  action: Action
) {
  return combineReducers({
    [fromConfiguration.featureKey]: fromConfiguration.reducer,
    [fromCourtesy.featureKey]: fromCourtesy.reducer,
    [fromFee.featureKey]: fromFee.reducer,
    [fromPatientExam.featureKey]: fromPatientExam.reducer,
    [fromTreatmentContract.featureKey]: fromTreatmentContract.reducer,
    [fromTreatmentPlan.featureKey]: fromTreatmentPlan.reducer,

    [fromDiagnosisCode.featureKey]: fromDiagnosisCode.reducer,
    [fromDoctor.featureKey]: fromDoctor.reducer,
    [fromGuardian.featureKey]: fromGuardian.reducer,
    [fromPracticeCourtesy.featureKey]: fromPracticeCourtesy.reducer,
    [fromPracticeFee.featureKey]: fromPracticeFee.reducer,
    [fromProcedure.featureKey]: fromProcedure.reducer,
    [fromTreatment.featureKey]: fromTreatment.reducer,
    [fromTxCoordinator.featureKey]: fromTxCoordinator.reducer,

    [fromInsurancePolicy.featureKey]: fromInsurancePolicy.reducer,
    [fromInsurancePolicyBenefit.featureKey]: fromInsurancePolicyBenefit.reducer,
    [fromLineItem.featureKey]: fromLineItem.reducer,
    [fromTreatmentNotification.featureKey]: fromTreatmentNotification.reducer,
    [fromTreatmentPlanBenefit.featureKey]: fromTreatmentPlanBenefit.reducer,

    [fromCopayProcedureFee.featureKey]: fromCopayProcedureFee.reducer,

    [fromInsuranceCarrier.featureKey]: fromInsuranceCarrier.reducer,
    [fromImaxx.featureKey]: fromImaxx.reducer,
    [fromModuleConfiguration.featureKey]: fromModuleConfiguration.reducer,
  })(state, action);
}

/** Console log all actions and the resulting state. This is intended for use as middleware inside the MetaReducers. */
export function indexLogger(
  reducer: ActionReducer<State>
): ActionReducer<State> {
  return (state, action) => {
    const result = reducer(state, action);
    console.groupCollapsed(action.type);
    console.log("🎬 ACTION:", action);
    console.log("⏪ LAST STATE:", state);
    console.log("⏩ NEXT STATE:", result);
    console.log("🧮 DIFF:", diff(state, result));
    console.groupEnd();
    return result;
  };
}

export const selectTreatmentDetailState = createFeatureSelector<
  State,
  TreatmentDetailState
>(featureKey);
